import { isTrackingAllowed } from '$lib/global/consent/consentGate';
import { setTrackingProperty } from '$lib/helper';
import { embedOptions } from '$lib/Stores/stores';

import get from 'lodash/get';
import { get as svelteStoreGet } from 'svelte/store';

import type { Component } from '$lib/types/Components/SharedTypes';
import type { Input } from '$lib/types/Field';
import type { InputTypeMap } from '$lib/types/InputTypeMap';
import type { TrackFormSubmitArgs } from '$lib/types/tracking';
import type { MappedFormValues } from '$lib/types/tracking';

export const getNowISO = () => {
    return new Date().toISOString();
};

// Map the form values (from form submit) to its input fields
export const createMappedFormValues = ({
    values,
    formId,
    inputFieldList,
    inputMap,
}: {
    values: { [key: string]: any };
    formId: string;
    inputFieldList: Input[];
    inputMap: InputTypeMap;
}): MappedFormValues => {
    return Object.keys(values)
        .filter((fieldName) => !!values[fieldName])
        .map((fieldName) => {
            const inputId = inputMap?.byName?.[formId]?.[fieldName];
            const inputField = inputFieldList.find(({ componentId }: { componentId: string }) => {
                return componentId === inputId;
            });

            let value = values[fieldName];

            if (inputField) {
                if (inputField.inputType === 'checkbox' && typeof value === 'string') {
                    value = value === 'true';
                }

                return {
                    inputField: inputField,
                    value,
                    conversionTrigger: inputField.conversionTrigger,
                };
            }

            return null;
        })
        .filter(Boolean);
};

// Update trackingProperties in the Session storage with the form values for personalization
// Called after submitting a form
export const updateTrackingProperties = ({
    form,
    values,
    fields,
    inputMap,
}: Omit<TrackFormSubmitArgs, 'button'>) => {
    const formField = fields.find(({ additional }) => {
        return get(additional, 'form.componentId') === form._id;
    });

    if (!formField) {
        return;
    }

    const campaignId = formField.campaignId;
    const inputFieldList = get(formField, 'additional.form.inputs');

    // Map the form values to the input fields
    const mappedFormValues = createMappedFormValues({
        values,
        formId: form._id,
        inputFieldList,
        inputMap,
    });

    mappedFormValues.forEach((mappedFormValue) => {
        if (mappedFormValue) {
            setTrackingProperty(
                mappedFormValue.inputField.fieldName,
                mappedFormValue.value,
                campaignId,
            );
        }
    });
};

/**
 * Map form values from input names to tracking field names.
 * Used by both analytics (v4.formSubmit) and personalization (updateTrackingProperties).
 */
export const mapFormValuesToFieldNames = (
    form: Component,
    values: Record<string, any>,
    inputMap: InputTypeMap,
): Record<string, string> => {
    const mapped: Record<string, string> = {};

    Object.keys(values).forEach((key) => {
        const value = values[key];
        const inputId = inputMap?.byName?.[form._id]?.[key];
        const input = form.components.find((c) => inputId === c._id);

        if (input) {
            mapped[input.content.fieldName] = value;
        }
    });

    if (values.fullAddress && values.address) {
        mapped['address.street'] = values.address.street;
        mapped['address.city'] = values.address.city;
        mapped['address.country'] = values.address.country;
        mapped['address.postalCode'] = values.address.postalCode;
    }

    return mapped;
};

/**
 * Environment-level delivery check, independent of consent.
 *
 * Split out of `shouldTrack()` for CAP-4169: the consent-free lead channel
 * (`global/consent/leadCapture.ts`) must honour the same dev and preview rules, but it runs
 * precisely when consent is *not* granted, so it cannot go through `shouldTrack()`.
 */
export const canDeliverEvents = (): boolean => {
    const isDev = import.meta.env.DEV;
    const options = svelteStoreGet(embedOptions);

    // The e2e suite sets __forceTracking to exercise the real pipeline against the dev server.
    if (isDev && !(window as any).__forceTracking) {
        return false;
    }

    return !options?.preview;
};

export const shouldTrack = () => {
    if (!canDeliverEvents()) {
        return false;
    }

    // CAP-4147 / § 25 TDDDG: no funnel event may leave the browser before the visitor
    // has consented. This is the backstop for the whole event pipeline — both the
    // Segment calls in v4.calls.ts and the first-party /ps-fnl-events queue in
    // core/perspective-tracking-api.ts route through here.
    return isTrackingAllowed();
};
